import type { components } from "@medimo/core-api/internal/openapi";
import { create } from "zustand";
import { devtools, persist } from "zustand/middleware";
import { immer } from "zustand/middleware/immer";

type ConsultProcess = components["schemas"]["ConsultProcess"];

export const AUDIO_PROCESSING_STATES: ReadonlySet<ConsultProcess> =
  new Set<ConsultProcess>([
    "uploading",
    "uploaded",
    "prerecorded_audio_processing",
  ]);

export const PRERECORDED_AUDIO_STATES = {
  /**
   * trigger-prerecorded-audio API の呼び出しに失敗した状態
   *
   * この状態の意味：
   * - 音声ファイルの S3 アップロードは成功
   * - バックエンドに音声処理開始を通知する trigger-prerecorded-audio API の呼び出しが失敗
   * - 原因：ネットワークエラー、一時的なサーバーエラー、認証エラーなど
   *
   * 必要な操作：
   * - 自動復旧：30分後に診察ページを訪問すると自動的に API を再呼び出し
   * - バックエンドは書き起こし済みチェックにより重複実行を抑制
   * - 注意：同時実行された場合、後から完了した処理のデータで上書きされる可能性あり
   */
  TRIGGER_PRERECORDED_AUDIO_FAILED: "trigger-prerecorded-audio-failed",

  /**
   * S3 への音声ファイルアップロードに失敗した状態
   *
   * この状態の意味：
   * - 録音した音声ファイルを S3 にアップロードする際にエラーが発生
   * - 主な原因：ネットワーク接続の問題、S3 の一時的な障害、アップロード中の接続切断
   * - 注意：音声ファイルの破損はこの段階では検出されない（バックエンド処理で検出される）
   *
   * 必要な操作：
   * - エラー画面になり、ユーザーによる手動での再アップロードが必要
   */
  S3_UPLOAD_FAILED: "s3-upload-failed",
} as const;

export type PrerecordedAudioState =
  (typeof PRERECORDED_AUDIO_STATES)[keyof typeof PRERECORDED_AUDIO_STATES];

type PrerecordedAudioStateStore = {
  // 診察 ID ごとの音声処理状態
  consultPrerecordedAudioStates: Record<string, PrerecordedAudioState>;

  getConsultPrerecordedAudioState: (
    consultId: string,
  ) => PrerecordedAudioState | undefined;

  setConsultPrerecordedAudioState: (
    consultId: string,
    state: PrerecordedAudioState,
  ) => void;

  clearConsultPrerecordedAudioState: (consultId: string) => void;
};

export const usePrerecordedAudioStateStore =
  create<PrerecordedAudioStateStore>()(
    devtools(
      persist(
        immer((set, get) => ({
          consultPrerecordedAudioStates: {},

          getConsultPrerecordedAudioState: (consultId: string) => {
            return get().consultPrerecordedAudioStates[consultId];
          },

          setConsultPrerecordedAudioState: (
            consultId: string,
            state: PrerecordedAudioState,
          ) => {
            set((draft) => {
              draft.consultPrerecordedAudioStates[consultId] = state;
            });
          },

          clearConsultPrerecordedAudioState: (consultId: string) => {
            set((draft) => {
              delete draft.consultPrerecordedAudioStates[consultId];
            });
          },
        })),
        {
          name: "medimo-consult-prerecorded-audio-state",
          version: 1,
        },
      ),
    ),
  );
